import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icons';

// Shared form controls: richer than plain checkboxes and selects.

/** On/off switch with a label and an optional one-line explanation. */
export function Toggle({ checked, onChange, label, hint, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean;
}) {
  return (
    <label className={`toggle ${disabled ? 'disabled' : ''}`}>
      <span className="toggle-text">
        <span className="toggle-label">{label}</span>
        {hint && <span className="toggle-hint">{hint}</span>}
      </span>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="switch" aria-hidden="true" />
    </label>
  );
}

/** − value + control for small integers. */
export function Stepper({ value, onChange, min = 0, max = 99, step = 1, suffix, label }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; suffix?: string; label?: string;
}) {
  const clamp = (v: number) => Math.max(min, Math.min(max, Math.round(v / step) * step));
  return (
    <span className="stepper" aria-label={label}>
      <button type="button" onClick={() => onChange(clamp(value - step))} disabled={value <= min} aria-label="Decrease"><Icon name="minus" size={14} /></button>
      <input type="number" value={value} min={min} max={max} step={step} onChange={(e) => onChange(clamp(+e.target.value || 0))} />
      {suffix && <span className="stepper-suffix">{suffix}</span>}
      <button type="button" onClick={() => onChange(clamp(value + step))} disabled={value >= max} aria-label="Increase"><Icon name="plus" size={14} /></button>
    </span>
  );
}

/** A labelled row: name on the left, control on the right. */
export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="field">
      <span className="toggle-text">
        <span className="toggle-label">{label}</span>
        {hint && <span className="toggle-hint">{hint}</span>}
      </span>
      {children}
    </div>
  );
}

export function Segmented<T extends string | number>({ value, onChange, options, small }: {
  value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; title?: string }[]; small?: boolean;
}) {
  return (
    <div className={`seg ${small ? 'small' : ''}`} role="radiogroup">
      {options.map((o) => (
        <button type="button" key={String(o.value)} role="radio" aria-checked={value === o.value} title={o.title}
          className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export interface Choice<T> { value: T; title: ReactNode; desc?: ReactNode; visual?: ReactNode; badge?: ReactNode; disabled?: boolean; className?: string; style?: CSSProperties }

/** Large selectable cards (single choice). */
export function ChoiceCards<T extends string | number>({ value, onChange, options, className }: {
  value: T; onChange: (v: T) => void; options: Choice<T>[]; className?: string;
}) {
  return (
    <div className={`choice-grid ${className ?? ''}`} role="radiogroup">
      {options.map((o) => (
        <button type="button" key={String(o.value)} role="radio" aria-checked={value === o.value} disabled={o.disabled}
          className={`choice ${value === o.value ? 'on' : ''} ${o.className ?? ''}`} style={o.style} onClick={() => onChange(o.value)}>
          {o.visual && <span className="choice-visual">{o.visual}</span>}
          <span className="choice-body">
            <span className="choice-title">{o.title}{o.badge}</span>
            {o.desc && <span className="choice-desc">{o.desc}</span>}
          </span>
          <span className="choice-check" aria-hidden="true"><Icon name="check" size={12} /></span>
        </button>
      ))}
    </div>
  );
}

/** Multi-select chips with optional icons. */
export function Chips({ options, selected, onChange }: {
  options: { value: string; label: ReactNode; icon?: ReactNode }[]; selected: string[]; onChange: (v: string[]) => void;
}) {
  return (
    <div className="chips">
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button type="button" key={o.value} aria-pressed={on} className={`chip ${on ? 'on' : ''}`}
            onClick={() => onChange(on ? selected.filter((v) => v !== o.value) : [...selected, o.value])}>
            {o.icon && <span className="chip-icon">{o.icon}</span>}{o.label}
          </button>
        );
      })}
    </div>
  );
}
